import React from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Col,
  Form,
  Image,
  OverlayTrigger,
  Popover,
  Row,
  Table,
  Tooltip,
} from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { anchovy, salmon } from 'lib/css/colors';
import Container from 'components/shared/Container';
import { createGoogleSearchUrl } from 'lib/utils/webSearchUtils';
import { greyBackground } from 'lib/css/colors';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import pointerOnHover from 'lib/css/pointerOnHover';
import PrivateLabelBrandBadge from 'components/manage_brands/brand_details/PrivateLabelBrandBadge';
import VerificationStatusIcon from 'components/global_products/detail_view/VerificationStatusIcon';

function DuplicateBrandsTable({
  areAllOnPageSelected,
  handleSelectAllOnPage,
  areSomeOnPageSelected,
  loadingDuplicateBrandClusters,
  brands,
  updatingBrands,
  toggleBrandSelected,
  isBrandSelected,
  setSelectedBrandForProducts,
  setShowBrandProductsModal,
}) {
  return (
    <Container>
      <Row>
        <Col md={12}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={areAllOnPageSelected()}
                    onChange={handleSelectAllOnPage}
                    ref={input => {
                      if (input) {
                        input.indeterminate = areSomeOnPageSelected();
                      }
                    }}
                    disabled={loadingDuplicateBrandClusters || !brands || brands.length === 0}
                  />
                </th>
                <th className={css(styles.logoHeader)}>Logo</th>
                <th>Brand</th>
                <th>Product Count</th>
                <th>manufacturer</th>
              </tr>
            </thead>
            {loadingDuplicateBrandClusters || updatingBrands ? (
              <tr>
                <td colSpan={6}>
                  <LoadingSpinner />
                </td>
              </tr>
            ) : (
              <tbody>
                {brands?.map(brand => (
                  <tr
                    key={brand.id}
                    onClick={() => toggleBrandSelected(brand, !isBrandSelected(brand))}
                    className={css(styles.clickableRow)}
                  >
                    <td onClick={() => toggleBrandSelected(brand, !isBrandSelected(brand))}>
                      <Form.Check
                        type="checkbox"
                        checked={isBrandSelected(brand)}
                        onClick={e => e.stopPropagation()}
                        onChange={e => toggleBrandSelected(brand, e.target.checked)}
                      />
                    </td>
                    <td className={css(styles.logoCell)}>
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Popover id={`popover-brand-logo-${brand?.id}`} style={{ maxWidth: 400 }}>
                            <Popover.Content>
                              <Image
                                width={400}
                                fluid
                                src={brand?.logo_url || placeholderProductImage}
                              />
                            </Popover.Content>
                          </Popover>
                        }
                      >
                        <Image
                          src={brand?.logo_url || placeholderProductImage}
                          width={50}
                          height={50}
                          thumbnail
                          onError={e => {
                            e.target.onerror = null;
                            e.target.src = placeholderProductImage;
                          }}
                        />
                      </OverlayTrigger>
                    </td>
                    <td>
                      <Button
                        variant="link"
                        onClick={e => {
                          e.stopPropagation();
                          window.open(
                            // eslint-disable-next-line
                            `/enrich-products/brand-details/${brand?.id}`,
                            '_blank',
                          );
                        }}
                      >
                        {brand?.name || '-'}
                      </Button>
                      <VerificationStatusIcon verificationStatus={brand?.verification_status} />
                      <PrivateLabelBrandBadge brand={brand} />
                      <OverlayTrigger overlay={<Tooltip>Search Brand on Google</Tooltip>}>
                        <FontAwesomeIcon
                          icon={faSearch}
                          style={{ color: anchovy }}
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            window.open(createGoogleSearchUrl([brand?.name]), '_blank');
                          }}
                          className={classNames(pointerOnHover, 'ml-2')}
                        />
                      </OverlayTrigger>
                      <Button
                        variant="link"
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedBrandForProducts(brand);
                          setShowBrandProductsModal(true);
                        }}
                      >
                        View Products
                      </Button>
                    </td>
                    <td>{brand?.product_count || '-'}</td>
                    <td>
                      {brand.global_manufacturer ? (
                        <>
                          <Button
                            variant="link"
                            onClick={e => {
                              e.stopPropagation();
                              window.open(
                                // eslint-disable-next-line
                                `/enrich-products/manufacturer-details/${brand?.global_manufacturer?.id}`,
                                '_blank',
                              );
                            }}
                          >
                            {brand?.global_manufacturer?.name || '-'}
                          </Button>
                          <VerificationStatusIcon
                            verificationStatus={brand?.global_manufacturer?.verification_status}
                          />
                          <OverlayTrigger
                            overlay={<Tooltip>Search Manufacturer on Google</Tooltip>}
                          >
                            <FontAwesomeIcon
                              icon={faSearch}
                              style={{ color: anchovy }}
                              onClick={e => {
                                e.stopPropagation();
                                window.open(
                                  createGoogleSearchUrl([brand?.global_manufacturer?.name]),
                                  '_blank',
                                );
                              }}
                              className={classNames(pointerOnHover, 'ml-2')}
                            />
                          </OverlayTrigger>
                          <OverlayTrigger
                            overlay={<Tooltip>Search Brand and Manufacturer on Google</Tooltip>}
                          >
                            <FontAwesomeIcon
                              icon={faSearch}
                              style={{ color: salmon }}
                              onClick={e => {
                                e.stopPropagation();
                                window.open(
                                  createGoogleSearchUrl([
                                    brand?.name,
                                    'by',
                                    brand?.global_manufacturer?.name,
                                  ]),
                                  '_blank',
                                );
                              }}
                              className={classNames(pointerOnHover, 'ml-2')}
                            />
                          </OverlayTrigger>
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </Table>
        </Col>
      </Row>
    </Container>
  );
}

const styles = StyleSheet.create({
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: greyBackground,
    },
  },
  logoHeader: {
    width: 70,
    textAlign: 'center',
  },
  logoCell: {
    width: 70,
    padding: '4px 8px',
  },
});

DuplicateBrandsTable.propTypes = {
  areAllOnPageSelected: PropTypes.func.isRequired,
  handleSelectAllOnPage: PropTypes.func.isRequired,
  areSomeOnPageSelected: PropTypes.func.isRequired,
  loadingDuplicateBrandClusters: PropTypes.bool.isRequired,
  brands: PropTypes.array.isRequired,
  updatingBrands: PropTypes.bool.isRequired,
  toggleBrandSelected: PropTypes.func.isRequired,
  isBrandSelected: PropTypes.func.isRequired,
  setSelectedBrandForProducts: PropTypes.func.isRequired,
  setShowBrandProductsModal: PropTypes.func.isRequired,
};

export default DuplicateBrandsTable;
